/**
 * uiLocator — "show me where that is" as a one-line call from anywhere.
 *
 * A window event rather than a store or context so any component can ask for a
 * highlight without prop-drilling or subscribing: the Demo Guide's jargon terms
 * sit several layers deep inside a draggable panel, and the tour lives in a
 * portal outside the app tree. Same pattern as PlatformTour's requestTourOpen().
 */
import type { UiLocationKey } from './uiLocations';
import { usePanelStore, type AnalysisCardFocus } from '../../stores/usePanelStore';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useFilterStore } from '../../stores/useFilterStore';
import { useFusionStore } from '../../stores/useFusionStore';

const LOCATE_EVENT = 'cryptoflow-locate-ui';

/** Analysis-tab accordion to expand when locating a card inside it. */
const ANALYSIS_CARD_BY_KEY: Partial<Record<UiLocationKey, AnalysisCardFocus>> = {
    'analysis-auto-investigate': 'auto-investigate',
    'analysis-follow-money': 'follow-the-money',
    'analysis-macro': 'analyze-graph',
    'analysis-holistic': 'holistic-trace',
    'le-report': 'auto-investigate',
};

const ANALYSIS_SURFACE_KEYS = new Set<UiLocationKey>([
    'inspector-tab-analysis',
    'analysis-auto-investigate',
    'analysis-follow-money',
    'analysis-macro',
    'analysis-holistic',
    'le-report',
]);

/** Keys that intentionally keep the Graph filters portal open. */
const FILTER_PANEL_KEYS = new Set<UiLocationKey>(['graph-filters', 'header-filters']);

/**
 * Bring a conditional UI surface into existence before the spotlight looks for
 * it. Graph Legend used to live behind a closed ⋯ menu; Graph filters live in a
 * closed portal; Analysis cards live under a collapsed accordion below a 400px
 * Auto-Investigate panel — without opening the right surface first, "Show me"
 * rings an off-screen rect or falls back to words while the tour claims the
 * control is on screen.
 */
export function ensureUiReady(key: UiLocationKey): void {
    if (key === 'canvas-legend') {
        usePanelStore.getState().setShowLegend(true);
    }
    if (key === 'fusion-legend' || key === 'fusion-surface-filters') {
        useFusionStore.getState().setShowLegend(true);
    }
    if (FILTER_PANEL_KEYS.has(key)) {
        useFilterStore.getState().setShowFilterBar(true);
    }
    if (ANALYSIS_SURFACE_KEYS.has(key)) {
        useSelectionStore.getState().setActiveTab('analysis');
        const card = ANALYSIS_CARD_BY_KEY[key];
        if (card) {
            usePanelStore.getState().setAnalysisCardFocus(card);
        }
    }
}

/**
 * Close tour-opened surfaces that should not linger after leaving a focus or
 * advancing to the next step. Pass `except` when staying on a surface that
 * still needs the panel (e.g. another filters Show me).
 */
export function releaseUiSurfaces(except?: UiLocationKey): void {
    if (!except || !FILTER_PANEL_KEYS.has(except)) {
        useFilterStore.getState().setShowFilterBar(false);
    }
    // Legend is opened by ensureUiReady('canvas-legend') for tour/Show me —
    // close it when advancing so it does not obscure the next step.
    if (except !== 'canvas-legend') {
        usePanelStore.getState().setShowLegend(false);
    }
    if (except !== 'fusion-legend' && except !== 'fusion-surface-filters') {
        useFusionStore.getState().setShowLegend(false);
    }
}

/** Flash a registry-known UI element on screen, or explain where to find it. */
export function locateUi(key: UiLocationKey): void {
    ensureUiReady(key);
    window.dispatchEvent(new CustomEvent<UiLocationKey>(LOCATE_EVENT, { detail: key }));
}

/** Subscribe to locate requests. Returns an unsubscribe function. */
export function subscribeLocateUi(handler: (key: UiLocationKey) => void): () => void {
    const listener = (event: Event) => handler((event as CustomEvent<UiLocationKey>).detail);
    window.addEventListener(LOCATE_EVENT, listener);
    return () => window.removeEventListener(LOCATE_EVENT, listener);
}
